import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FormOutlined from '@ant-design/icons/FormOutlined'
import InfoCircleTwoTone from '@ant-design/icons/InfoCircleTwoTone'
import LeftOutlined from '@ant-design/icons/LeftOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import fontkit from '@pdf-lib/fontkit'
import {
  Button,
  Empty,
  Input,
  message,
  Modal,
  Space,
  Spin,
  Tooltip,
} from 'antd'
import { inject, observer } from 'mobx-react'
import { PDFDocument } from 'pdf-lib'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Stage } from 'react-konva'
import { Document, Page } from 'react-pdf'
import { blue } from '../../../../../color'
import {
  TitleContentBox,
  trimOverLengthString,
} from '../../../../../components/Common/CellText'
import ContentBlockWrapper from '../../../../../components/ContentBlockWrapper'
import {
  DocumentController,
  NameDocument,
} from '../../../../../components/PopupSign/PopupSignStyled'
import EditableText from '../EditableText'
import {
  DocumentWrapper,
  PopupRegisNumberWrapper,
} from './PopupRegisNumberStyled'

const { confirm } = Modal

const PopupRegisNumberAnnexDoc = props => {
  const {
    isVisiblePopupRegisNumber,
    electronicDocumentStore,
    fileActive,
    loadingAnimationStore,
    fileStore,
    handleClosePopupRegisNumber,
    handleAfterRegisNumber,
  } = props

  const { documentAttachment } = fileStore
  const { selectedElectronicDocument } = electronicDocumentStore

  const [styleCanvas, setStyleCanvas] = useState({
    width: 0,
    height: 60,
    border: null,
  })
  const [pageNumber, setPageNumber] = useState(1)
  const [fileBlob, setFileBlob] = useState(null)
  const [loadedPdf, setLoadedPdf] = useState()
  const [numPages, setNumPages] = useState(null)
  const stageRef = useRef(null)

  const [numberElectronic, setNumberElectronic] = useState({
    text: '001',
    position: { x: 50, y: 50 },
    fontSize: 12,
  })
  const [dayElectronic, setDayElectronic] = useState({
    text: '002',
    position: { x: 50, y: 100 },
    fontSize: 12,
  })
  const [monthElectronic, setMonthElectronic] = useState({
    text: '003',
    position: { x: 50, y: 150 },
    fontSize: 12,
  })

  function blobToFile(theBlob, fileName) {
    return new File([theBlob], fileName, {
      lastModified: new Date().getTime(),
      type: theBlob.type,
    })
  }

  useEffect(() => {
    if (!isVisiblePopupRegisNumber || !fileActive) return

    if (fileActive.id) {
      // Existing file with fileId
      ;(async () => {
        try {
          const blob = await fileStore.downloadAttachment(fileActive.id)
          setFileBlob(blobToFile(blob.data, fileActive.name))
          fileStore.setDocumentAttachment(
            new File([blob], fileActive.name, {
              lastModified: new Date().getTime(),
              type: blob.type,
            })
          )
        } catch (err) {
          // message.error('Không thể tải tệp. Vui lòng thử lại.')
          console.error('Error downloading file:', err)
        }
      })()
    } else if (fileActive.originFileObj) {
      // New file
      setFileBlob(fileActive.originFileObj)
      fileStore.setDocumentAttachment(fileActive.originFileObj)
      console.log('Using new file blob:', fileActive.name)
    } else {
      // message.error('Không có tệp hợp lệ để cấp số.')
      handleClosePopupRegisNumber()
    }
  }, [isVisiblePopupRegisNumber, fileActive, fileStore])

  useEffect(() => {
    if (!fileBlob) {
      setStyleCanvas({
        width: 0,
        height: 60,
        border: null,
      })
    }
  }, [fileBlob])

  const renderPage = useCallback(
    async (pdf, pageNumber) => {
      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 1 })
      setStyleCanvas({
        width: viewport.width,
        height: viewport.height,
        border: '1px solid #000',
      })
    },
    [setStyleCanvas]
  )

  const clearDataPopupRegisNumber = useCallback(() => {
    setPageNumber(1)
    setNumberElectronic({
      text: '001',
      position: { x: 50, y: 50 },
      fontSize: 12,
    })
    setDayElectronic({
      text: '002',
      position: { x: 50, y: 100 },
      fontSize: 12,
    })
    setMonthElectronic({
      text: '003',
      position: { x: 50, y: 150 },
      fontSize: 12,
    })
    setFileBlob(null)
    fileStore.clearDocumentAttachment()
  }, [
    setPageNumber,
    setNumberElectronic,
    setDayElectronic,
    setMonthElectronic,
    setFileBlob,
    fileStore,
  ])

  const onDocumentLoadSuccess = useCallback(
    pdf => {
      const { numPages } = pdf
      setNumPages(numPages)
      setLoadedPdf(pdf)
      renderPage(pdf, pageNumber)
    },
    [setNumPages, setLoadedPdf, renderPage, pageNumber]
  )

  const handleChangeIncomingNumber = useCallback(event => {
    let newText = ''
    if (event) {
      if (event.value) {
        newText = event.text
      } else {
        newText = event.target.value
      }
      setNumberElectronic(prevState => ({
        ...prevState,
        text: newText,
      }))
    }
  }, [])

  const handleChangeDayElectronic = useCallback(event => {
    let newText = ''
    if (event) {
      if (event.value) {
        newText = event.text
      } else {
        newText = event.target.value
      }
      setDayElectronic(prevState => ({
        ...prevState,
        text: newText,
      }))
    }
  }, [])

  const handleChangeMonthElectronic = useCallback(event => {
    let newText = ''
    if (event) {
      if (event.value) {
        newText = event.text
      } else {
        newText = event.target.value
      }
      setMonthElectronic(prevState => ({
        ...prevState,
        text: newText,
      }))
    }
  }, [])

  const handleRegisNumber = useCallback(async () => {
    if (!fileBlob) {
      message.error('Không có tệp để cấp số.')
      return
    }
    try {
      loadingAnimationStore.showSpinner(true)
      const arrayBuffer = await fileBlob.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer)
      pdfDoc.registerFontkit(fontkit)
      const pages = pdfDoc.getPages()
      const renderPage = pages[pageNumber - 1]
      const fontBytes = await fetch('/assets/fonts/times.ttf').then(res =>
        res.arrayBuffer()
      )
      const font = await pdfDoc.embedFont(fontBytes)
      const { width, height } = renderPage.getSize()
      renderPage.drawText(numberElectronic.text, {
        x: numberElectronic.position.x,
        y: height - numberElectronic.position.y - numberElectronic.fontSize,
        size: numberElectronic.fontSize,
        font,
      })
      renderPage.drawText(dayElectronic.text, {
        x: dayElectronic.position.x,
        y: height - dayElectronic.position.y - dayElectronic.fontSize,
        size: dayElectronic.fontSize,
        font,
      })
      renderPage.drawText(monthElectronic.text, {
        x: monthElectronic.position.x,
        y: height - monthElectronic.position.y - monthElectronic.fontSize,
        size: monthElectronic.fontSize,
        font,
      })
      const pdfBytes = await pdfDoc.save()
      const formData = new FormData()
      formData.append(
        'file',
        new Blob([pdfBytes], { type: 'application/pdf' }),
        fileActive.name
      )
      const response = await fileStore.uploadFile(formData)
      handleAfterRegisNumber(response.data.file_id, response.data.file_name)
      clearDataPopupRegisNumber()
      handleClosePopupRegisNumber()
      message.success('Cấp số thành công!')
    } catch (err) {
      console.error('Error registering number:', err)
      message.error(err.vi || 'Đã có lỗi xảy ra khi cấp số!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    fileBlob,
    numberElectronic,
    dayElectronic,
    monthElectronic,
    pageNumber,
    fileActive,
    fileStore,
    handleAfterRegisNumber,
    clearDataPopupRegisNumber,
    handleClosePopupRegisNumber,
  ])

  const showRegisNumberConfirm = useCallback(() => {
    confirm({
      title: 'Bạn có chắc chắn cấp số tài liệu này?',
      icon: <InfoCircleTwoTone />,
      content: '',
      okText: 'Đồng ý',
      okType: 'primary',
      cancelText: 'Hủy bỏ',
      onOk() {
        handleRegisNumber()
      },
      onCancel() {
        console.log('Hủy cấp số')
      },
    })
  }, [handleRegisNumber])

  const headerRegisNumber = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <span>Cấp số tài liệu</span>
        <Space>
          <Button
            onClick={showRegisNumberConfirm}
            icon={<FormOutlined />}
            type="primary">
            Cấp số tài liệu
          </Button>
          <Tooltip title="Đóng">
            <Button
              icon={<CloseOutlined />}
              danger
              onClick={() => {
                clearDataPopupRegisNumber()
                handleClosePopupRegisNumber()
              }}
            />
          </Tooltip>
        </Space>
      </div>
    ),
    [
      showRegisNumberConfirm,
      clearDataPopupRegisNumber,
      handleClosePopupRegisNumber,
    ]
  )

  return (
    <PopupRegisNumberWrapper
      visible={isVisiblePopupRegisNumber}
      footer={null}
      width={1300}
      style={{ top: 20 }}
      title={headerRegisNumber}
      closable={false}>
      <div style={{ display: 'flex', columnGap: 20 }}>
        <div style={{ flexGrow: 1 }}>
          <DocumentWrapper>
            <DocumentController maxWidth={styleCanvas.width}>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <Button
                  style={{ width: 42 }}
                  onClick={() => {
                    if (pageNumber === 1) return
                    setPageNumber(pageNumber - 1)
                    renderPage(loadedPdf, pageNumber - 1)
                  }}
                  icon={<LeftOutlined />}
                />
                <p style={{ margin: '0 10px' }}>
                  {pageNumber}/ {numPages}
                </p>
                <Button
                  style={{ width: 42 }}
                  onClick={() => {
                    if (pageNumber === numPages) return
                    setPageNumber(pageNumber + 1)
                    renderPage(loadedPdf, pageNumber + 1)
                  }}
                  icon={<RightOutlined />}
                />
              </div>
            </DocumentController>
            <NameDocument maxWidth={styleCanvas.width} signed={false}>
              <Tooltip title={fileActive?.name}>
                <div
                  onClick={() =>
                    fileActive.id &&
                    fileStore.handleDownloadFile(fileActive.id, fileActive.name)
                  }>
                  <DownloadOutlined
                    style={{
                      color: blue,
                      fontSize: 16,
                      marginRight: 10,
                    }}
                  />
                  <span style={{ lineHeight: '20px' }}>
                    {trimOverLengthString(fileActive?.file_name, 70)}
                  </span>
                </div>
              </Tooltip>
            </NameDocument>
            {fileBlob ? (
              <div className={'document-wrapper'}>
                <Document
                  style={{ display: 'flex', justifyContent: 'center' }}
                  file={fileBlob}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className={'spin-box'}>
                      <Spin tip="Đang tải tài liệu" />
                    </div>
                  }>
                  <Page pageNumber={pageNumber} loading={null} />
                </Document>
                <Stage
                  ref={stageRef}
                  width={styleCanvas.width}
                  height={styleCanvas.height}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    border: styleCanvas.border,
                    top: 0,
                    zIndex: 9,
                  }}>
                  <Layer>
                    <>
                      <EditableText
                        stageRef={stageRef}
                        value={numberElectronic}
                        setValue={setNumberElectronic}
                      />
                      <EditableText
                        stageRef={stageRef}
                        value={dayElectronic}
                        setValue={setDayElectronic}
                      />
                      <EditableText
                        stageRef={stageRef}
                        value={monthElectronic}
                        setValue={setMonthElectronic}
                      />
                    </>
                  </Layer>
                </Stage>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={'Vui lòng chọn tài liệu tài liệu!'}
              />
            )}
          </DocumentWrapper>
        </div>
        <div style={{ flex: '0 0 300px', maxWidth: 300 }}>
          <ContentBlockWrapper style={{ marginBottom: 20 }}>
            <TitleContentBox>Tùy chọn cấp số</TitleContentBox>
            <div
              style={{
                marginBottom: '1rem',
                alignItems: 'center',
              }}>
              <label style={{ fontWeight: 600, marginRight: 10, width: 60 }}>
                Số:{''}
              </label>
              <Input
                placeholder="Nhập số"
                maxLength={10}
                value={numberElectronic.text}
                onChange={handleChangeIncomingNumber}
              />
            </div>
            <div
              style={{
                marginBottom: '1rem',
                alignItems: 'center',
              }}>
              <label style={{ fontWeight: 600, marginRight: 10, width: 60 }}>
                Ngày:{' '}
              </label>
              <Input
                placeholder="Nhập ngày"
                maxLength={10}
                value={dayElectronic.text}
                onChange={handleChangeDayElectronic}
              />
            </div>
            <div
              style={{
                marginBottom: '1rem',
                alignItems: 'center',
              }}>
              <label style={{ fontWeight: 600, marginRight: 10, width: 60 }}>
                Tháng:{' '}
              </label>
              <Input
                placeholder="Nhập tháng"
                maxLength={10}
                value={monthElectronic.text}
                onChange={handleChangeMonthElectronic}
              />
            </div>
          </ContentBlockWrapper>
        </div>
      </div>
    </PopupRegisNumberWrapper>
  )
}

export default inject(
  'signedDocumentStore',
  'loadingAnimationStore',
  'authenticationStore',
  'fileStore',
  'electronicDocumentStore'
)(observer(PopupRegisNumberAnnexDoc))
